import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FlaskConical, ChefHat, Store, Warehouse, Trash2, SquarePen, Search } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { outletService } from '../../../../services/outletService';
import { menuItemService } from '../../../../services/menuItemService';
import { recipeService, WarehouseProduct } from '../../../../services/recipeService';
import { apiService } from '../../../../services/api';
import type { OutletListDTO, MenuItemListDTO, MenuItemDTO, CreateMenuItemRecipeDTO } from '../../../../types';
import '../RestaurantPage.css';

import { getServerErrorMessage } from '../../../../utils/errorUtils';
// ── Local types ────────────────────────────────────────────────────────────────

interface UnitOption { id: number; name: string; shortName?: string; }

interface RecipeRow {
  key:       number;
  productId: number | '';
  unitId:    number | '';
  quantity:  number;
  notes:     string;
}

// ── Component ──────────────────────────────────────────────────────────────────

const RecipeReferencePage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  // ── Reference data ─────────────────────────────────────────────────────────
  const [outlets,    setOutlets]    = useState<OutletListDTO[]>([]);
  const [menuItems,  setMenuItems]  = useState<MenuItemListDTO[]>([]);
  const [units,      setUnits]      = useState<UnitOption[]>([]);
  const [products,   setProducts]   = useState<WarehouseProduct[]>([]);   // scoped to outlet warehouse
  const [allProducts, setAllProducts] = useState<WarehouseProduct[]>([]); // unfiltered fallback

  // ── Selection state ────────────────────────────────────────────────────────
  const [selectedOutletId,  setSelectedOutletId]  = useState<number | ''>('');
  const [selectedItemId,    setSelectedItemId]    = useState<number | null>(null);
  const [selectedItemData,  setSelectedItemData]  = useState<MenuItemDTO | null>(null);

  // ── Recipe editing state ───────────────────────────────────────────────────
  const [recipes,    setRecipes]    = useState<RecipeRow[]>([]);
  const [rowKey,     setRowKey]     = useState(0);
  const [isDirty,    setIsDirty]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState<string | null>(null);
  const [saveOk,     setSaveOk]     = useState(false);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [search,     setSearch]     = useState('');
  const [filterCat,  setFilterCat]  = useState<string>('all');

  // ── Loading ────────────────────────────────────────────────────────────────
  const [loadingItem,  setLoadingItem]  = useState(false);
  const [loadingProds, setLoadingProds] = useState(false);
  const [pageLoading,  setPageLoading]  = useState(true);

  // ── Bootstrap: outlets, menu items, units ─────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      setPageLoading(true);
      try {
        const [outletRes, itemRes, unitRes, allProdRes] = await Promise.all([
          outletService.getAll(),
          menuItemService.getAll(1, 500),
          apiService.get('/units', { pageNumber: 1, pageSize: 200 }),
          apiService.get('/products', { pageNumber: 1, pageSize: 1000 }),
        ]);

        const outletData = (outletRes.data as any)?.data ?? outletRes.data ?? [];
        setOutlets(Array.isArray(outletData) ? outletData : []);

        const itemRaw = (itemRes.data as any)?.data ?? (itemRes.data as any) ?? [];
        const itemData = Array.isArray(itemRaw) ? itemRaw : (itemRaw?.data ?? []);
        setMenuItems(Array.isArray(itemData) ? itemData : []);

        const unitData = (unitRes.data as any)?.data ?? unitRes.data ?? [];
        setUnits(
          Array.isArray(unitData)
            ? unitData.map((u: any) => ({ id: u.id, name: u.name ?? u.unitName, shortName: u.shortName ?? u.abbreviation }))
            : [],
        );

        const prodData = (allProdRes.data as any)?.data ?? allProdRes.data ?? [];
        const mapped: WarehouseProduct[] = Array.isArray(prodData)
          ? prodData.map((p: any) => ({ id: p.id, name: p.name, sku: p.sku ?? p.SKU ?? '' }))
          : [];
        setAllProducts(mapped);
        setProducts(mapped); // show all when no outlet selected
      } catch {
        // non-fatal
      } finally {
        setPageLoading(false);
      }
    };
    fetchAll();
  }, []);

  // ── Outlet change: reload scoped products ──────────────────────────────────
  useEffect(() => {
    const outlet = outlets.find(o => o.id === selectedOutletId);
    if (!outlet?.warehouseId) {
      setProducts(allProducts);
      return;
    }
    setLoadingProds(true);
    recipeService
      .getProductsByWarehouse(outlet.warehouseId)
      .then((res) => {
        const data = (res.data as any)?.data ?? res.data ?? [];
        setProducts(Array.isArray(data) ? data : allProducts);
      })
      .catch(() => setProducts(allProducts))
      .finally(() => setLoadingProds(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutletId, outlets]);

  // ── Load selected menu item detail (with recipes) ─────────────────────────
  const loadItem = useCallback(async (id: number) => {
    setLoadingItem(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      const res  = await menuItemService.getById(id);
      const data = (res.data as any)?.data ?? res.data;
      setSelectedItemData(data);
      let k = rowKey;
      const rows: RecipeRow[] = (data?.recipes ?? []).map((r: any) => ({
        key:       k++,
        productId: r.productId ?? '',
        unitId:    r.unitId    ?? '',
        quantity:  r.quantity  ?? 1,
        notes:     r.notes     ?? '',
      }));
      setRecipes(rows);
      setRowKey(k);
      setIsDirty(false);
    } catch {
      setSelectedItemData(null);
    } finally {
      setLoadingItem(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectItem = (id: number) => {
    if (id === selectedItemId) return;
    setSelectedItemId(id);
    loadItem(id);
  };

  // ── Recipe row mutations ───────────────────────────────────────────────────
  const addRow = () => {
    setRecipes(prev => [...prev, { key: rowKey, productId: '', unitId: '', quantity: 1, notes: '' }]);
    setRowKey(k => k + 1);
    setIsDirty(true);
  };

  const removeRow = (key: number) => {
    setRecipes(prev => prev.filter(r => r.key !== key));
    setIsDirty(true);
  };

  const updateRow = (key: number, field: keyof RecipeRow, value: string | number) => {
    setRecipes(prev =>
      prev.map(r =>
        r.key === key
          ? {
              ...r,
              [field]:
                field === 'productId' || field === 'unitId'
                  ? (value === '' ? '' : Number(value))
                  : field === 'quantity' ? Number(value)
                  : value,
            }
          : r,
      ),
    );
    setIsDirty(true);
  };

  // ── Save recipe ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedItemId) return;
    setSaveError(null);
    setSaveOk(false);
    setSaving(true);
    const payload: CreateMenuItemRecipeDTO[] = recipes
      .filter(r => r.productId !== '' && r.unitId !== '' && r.quantity > 0)
      .map(r => ({
        productId: r.productId as number,
        unitId:    r.unitId    as number,
        quantity:  r.quantity,
        notes:     r.notes.trim() || undefined,
      }));
    try {
      await menuItemService.updateRecipes(selectedItemId, payload);
      setIsDirty(false);
      setSaveOk(true);
      // Refresh the menu item list count
      const listRes = await menuItemService.getAll(1, 500);
      const listData = (listRes.data as any)?.data?.data ?? (listRes.data as any)?.data ?? [];
      if (Array.isArray(listData)) setMenuItems(listData);
    } catch (err: any) {
      setSaveError(getServerErrorMessage(err) ?? t('restaurant.recipe.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ── Derived: outlet info ───────────────────────────────────────────────────
  const selectedOutlet = outlets.find(o => o.id === selectedOutletId);

  // ── Derived: categories for filter ────────────────────────────────────────
  const categories = Array.from(new Set(menuItems.map(i => i.categoryName))).sort();

  // ── Derived: filtered menu item list ──────────────────────────────────────
  const filteredItems = menuItems.filter(i => {
    const matchCat  = filterCat === 'all' || i.categoryName === filterCat;
    const matchSrch = !search.trim() || i.name.toLowerCase().includes(search.trim().toLowerCase());
    return matchCat && matchSrch;
  });

  // ── Helpers ────────────────────────────────────────────────────────────────
  const productName = (id: number | '') =>
    id === '' ? '—' : (products.find(p => p.id === id)?.name ?? allProducts.find(p => p.id === id)?.name ?? `#${id}`);

  const unitName = (id: number | '') =>
    id === '' ? '—' : (units.find(u => u.id === id)?.name ?? `#${id}`);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="ims-create-body-page rest-create-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate('/restaurant/menu-items')}>
              ← {t('restaurant.menuItem.title')}
            </button>
            <div>
              <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FlaskConical size={20} style={{ color: 'var(--ims-accent)' }} />
                {t('restaurant.recipe.referencePage')}
              </h1>
              <p className="ims-page-subtitle">{t('restaurant.recipe.referenceSubtitle')}</p>
            </div>
          </div>

          {/* Outlet selector */}
          <div className="ims-top-bar-right" style={{ gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Store size={16} style={{ color: 'var(--ims-text-sub)', flexShrink: 0 }} />
              <select
                className="ims-form-control"
                style={{ minWidth: 200, padding: '6px 10px', fontSize: 13 }}
                value={selectedOutletId}
                onChange={e => setSelectedOutletId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">{t('restaurant.recipe.selectOutlet')}</option>
                {outlets.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            {selectedOutlet && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 20,
                background: 'var(--ims-accent-bg)', border: '1px solid var(--ims-accent-ring)',
                fontSize: 12, color: 'var(--ims-accent)',
              }}>
                <Warehouse size={13} />
                {selectedOutlet.warehouseName
                  ? selectedOutlet.warehouseName
                  : <em style={{ opacity: 0.7 }}>{t('restaurant.outlet.noWarehouse')}</em>}
              </div>
            )}
          </div>
        </div>

        {/* ── Warehouse scope notice ── */}
        {selectedOutletId !== '' && !selectedOutlet?.warehouseId && (
          <div className="ims-alert ims-alert-warning" style={{ margin: '0 0 12px', fontSize: 13 }}>
            {t('restaurant.recipe.noWarehouseWarning')}
            {' '}
            <button
              className="ims-btn ims-btn-secondary"
              style={{ padding: '2px 10px', fontSize: 12, marginInlineStart: 8 }}
              onClick={() => navigate(`/restaurant/outlets/${selectedOutlet?.id}/edit`)}
            >
              {t('restaurant.recipe.configureOutlet')}
            </button>
          </div>
        )}

        {/* ── Master–detail body ── */}
        {pageLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ims-text-sub)' }}>
            {t('common.loading')}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

            {/* ════ LEFT PANEL: menu item list ════════════════════════════════ */}
            <div style={{
              width: 280, flexShrink: 0,
              display: 'flex', flexDirection: 'column', gap: 10,
              position: 'sticky', top: 16, maxHeight: 'calc(100vh - 160px)', overflow: 'hidden',
            }}>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ims-text-sub)' }} />
                <input
                  className="ims-form-control"
                  style={{ paddingInlineStart: 30, fontSize: 13 }}
                  placeholder={t('common.search')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              {/* Category filter */}
              <select
                className="ims-form-control"
                style={{ fontSize: 13, padding: '6px 10px' }}
                value={filterCat}
                onChange={e => setFilterCat(e.target.value)}
              >
                <option value="all">{t('restaurant.menuItem.allCategories')}</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              {/* Item list */}
              <div className="ims-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div className="ims-card-header" style={{ padding: '8px 12px' }}>
                  <span className="ims-card-title" style={{ fontSize: 12 }}>
                    <ChefHat size={13} style={{ verticalAlign: 'middle', marginInlineEnd: 5 }} />
                    {t('restaurant.menuItem.title')}
                    <span style={{ marginInlineStart: 6, fontWeight: 400, color: 'var(--ims-text-sub)' }}>
                      ({filteredItems.length})
                    </span>
                  </span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {filteredItems.length === 0 ? (
                    <p style={{ padding: '20px 12px', color: 'var(--ims-text-sub)', fontSize: 13, textAlign: 'center' }}>
                      {t('restaurant.menuItem.noItemsFound')}
                    </p>
                  ) : (
                    filteredItems.map(item => {
                      const isActive = item.id === selectedItemId;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleSelectItem(item.id)}
                          style={{
                            display: 'block', width: '100%', textAlign: 'start',
                            padding: '9px 12px',
                            background: isActive ? 'var(--ims-accent-bg)' : 'transparent',
                            borderInlineStart: `3px solid ${isActive ? 'var(--ims-accent)' : 'transparent'}`,
                            border: 'none',
                            borderBottom: '1px solid var(--ims-border)',
                            cursor: 'pointer',
                            transition: 'background 0.12s',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--ims-accent)' : 'var(--ims-text-main)', lineHeight: 1.3 }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ims-text-sub)', marginTop: 2 }}>
                            {item.categoryName}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* ════ RIGHT PANEL: recipe editor ════════════════════════════════ */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* Empty state */}
              {!selectedItemId && (
                <div className="ims-card" style={{ textAlign: 'center', padding: '60px 40px' }}>
                  <FlaskConical size={40} style={{ color: 'var(--ims-text-sub)', opacity: 0.4, marginBottom: 12 }} />
                  <p style={{ fontWeight: 600, color: 'var(--ims-text-sub)' }}>
                    {t('restaurant.recipe.selectItemPrompt')}
                  </p>
                </div>
              )}

              {/* Loading item */}
              {selectedItemId && loadingItem && (
                <div className="ims-card" style={{ padding: 40, textAlign: 'center', color: 'var(--ims-text-sub)' }}>
                  {t('common.loading')}
                </div>
              )}

              {/* Item detail + recipe editor */}
              {selectedItemId && !loadingItem && selectedItemData && (
                <>
                  {/* Item header */}
                  <div className="ims-card">
                    <div className="ims-card-body" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                          background: 'var(--ims-accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <ChefHat size={22} style={{ color: 'var(--ims-accent)' }} />
                        </div>
                        <div>
                          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--ims-text-main)' }}>
                            {selectedItemData.name}
                          </h2>
                          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ims-text-sub)' }}>
                            {selectedItemData.typeName && <span>{selectedItemData.typeName} → </span>}
                            {selectedItemData.categoryName}
                            {selectedItemData.prepTimeMinutes != null && (
                              <span> · {selectedItemData.prepTimeMinutes} min</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ims-accent)' }}>
                          {selectedItemData.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600,
                          background: selectedItemData.isAvailable ? 'var(--ims-green-bg)' : 'var(--ims-red-bg)',
                          color: selectedItemData.isAvailable ? 'var(--ims-green-text)' : 'var(--ims-red-text)',
                        }}>
                          {selectedItemData.isAvailable ? t('restaurant.menuItem.available') : t('restaurant.menuItem.unavailable')}
                        </span>
                        <button
                          className="ims-btn ims-btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => navigate(`/restaurant/menu-items/${selectedItemId}/edit`)}
                        >
                          <SquarePen size={13} style={{ verticalAlign: 'middle', marginInlineEnd: 4 }} />
                          {t('common.edit')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Recipe editor */}
                  <div className="ims-card">
                    <div className="ims-card-header">
                      <span className="ims-card-title">
                        <FlaskConical size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                        {t('restaurant.recipe.title')}
                      </span>
                      {loadingProds && (
                        <span style={{ fontSize: 11, color: 'var(--ims-text-sub)' }}>
                          {t('restaurant.recipe.loadingProducts')}…
                        </span>
                      )}
                    </div>
                    <div className="ims-card-body" style={{ overflowX: 'auto' }}>

                      {/* Warehouse scope banner */}
                      {selectedOutlet?.warehouseId && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '6px 10px', marginBottom: 12, borderRadius: 6,
                          background: 'var(--ims-accent-bg)', fontSize: 12, color: 'var(--ims-accent)',
                        }}>
                          <Warehouse size={13} />
                          {t('restaurant.recipe.warehouseScope')}: <strong>{selectedOutlet.warehouseName}</strong>
                        </div>
                      )}

                      <table className="rest-recipe-table">
                        <thead>
                          <tr>
                            <th>{t('restaurant.recipe.product')}</th>
                            <th style={{ width: 150 }}>{t('restaurant.recipe.unit')}</th>
                            <th style={{ width: 90 }}>{t('restaurant.recipe.quantity')}</th>
                            <th>{t('common.notes')}</th>
                            <th style={{ width: 44 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {recipes.length === 0 && (
                            <tr>
                              <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ims-text-sub)', padding: '1.5rem' }}>
                                {t('restaurant.recipe.noIngredients')}
                              </td>
                            </tr>
                          )}
                          {recipes.map(row => (
                            <tr key={row.key}>
                              <td>
                                <select
                                  className="rest-recipe-input"
                                  value={row.productId}
                                  onChange={e => updateRow(row.key, 'productId', e.target.value)}
                                >
                                  <option value="">{t('restaurant.recipe.selectProduct')}</option>
                                  {products.map(p => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}{p.sku ? ` (${p.sku})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="rest-recipe-input"
                                  value={row.unitId}
                                  onChange={e => updateRow(row.key, 'unitId', e.target.value)}
                                >
                                  <option value="">{t('restaurant.recipe.selectUnit')}</option>
                                  {units.map(u => (
                                    <option key={u.id} value={u.id}>
                                      {u.name}{u.shortName ? ` (${u.shortName})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="rest-recipe-input"
                                  value={row.quantity}
                                  min={0.001}
                                  step="0.001"
                                  onChange={e => updateRow(row.key, 'quantity', e.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="rest-recipe-input"
                                  value={row.notes}
                                  placeholder={t('common.notes')}
                                  maxLength={200}
                                  onChange={e => updateRow(row.key, 'notes', e.target.value)}
                                />
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className="rest-remove-btn"
                                  onClick={() => removeRow(row.key)}
                                  title={t('common.remove')}
                                >
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
                        <button type="button" className="rest-add-row-btn" onClick={addRow}>
                          + {t('restaurant.recipe.addIngredient')}
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {saveOk && !isDirty && (
                            <span style={{ fontSize: 12, color: 'var(--ims-green-text)' }}>
                              ✓ {t('restaurant.recipe.saved')}
                            </span>
                          )}
                          {saveError && (
                            <span style={{ fontSize: 12, color: 'var(--ims-red-text)' }}>
                              {saveError}
                            </span>
                          )}
                          <button
                            type="button"
                            className="ims-btn ims-btn-primary"
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                          >
                            {saving ? t('common.saving') : t('restaurant.recipe.saveRecipe')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Read-only summary (when not dirty) */}
                  {!isDirty && recipes.length > 0 && (
                    <div className="ims-card">
                      <div className="ims-card-header">
                        <span className="ims-card-title" style={{ fontSize: 12 }}>
                          {t('restaurant.recipe.ingredientSummary')}
                        </span>
                      </div>
                      <div className="ims-card-body" style={{ padding: '8px 12px' }}>
                        {recipes
                          .filter(r => r.productId !== '' && r.unitId !== '')
                          .map((r, idx) => (
                            <div key={r.key} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                              borderBottom: idx < recipes.length - 1 ? '1px solid var(--ims-border)' : 'none',
                              fontSize: 13,
                            }}>
                              <span style={{ flex: 1, fontWeight: 500 }}>{productName(r.productId)}</span>
                              <span style={{ color: 'var(--ims-text-sub)' }}>
                                {r.quantity} {unitName(r.unitId)}
                              </span>
                              {r.notes && (
                                <span style={{ fontSize: 11, color: 'var(--ims-text-sub)', fontStyle: 'italic' }}>
                                  {r.notes}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RecipeReferencePage;
